import { MapPin, Phone, Instagram, Facebook } from "lucide-react";
import LocalLink from "@/components/LocalLink";
import { useLanguage } from "@/contexts/LanguageContext";
import logoVertical from "@/assets/LOGO-ARANDIA-BRANCA-TRANSPARENT.png";

const socialLinks = [
  { icon: Instagram, href: "https://www.instagram.com/arandialaw/", label: "Instagram" },
  { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61572508148403", label: "Facebook" },
];

const Footer = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { name: t("nav.home"), href: "#home" },
    { name: t("nav.about"), href: "#about" },
    { name: t("nav.services"), href: "#services" },
    { name: t("insights.nav"), href: "/insights", isRoute: true },
    { name: t("nav.contact"), href: "#contact" },
  ];

  const locations = [
    {
      nameKey: "footer.location.queens",
      href: "/locations/new-york",
      address: "80-02 Kew Gardens Rd Suite 702",
      city: "Kew Gardens, NY 11415",
      phone: "(212) 586-0500",
      subway: "E/F (Kew Gardens/Union T.Pike)",
      subwayLabelKey: "footer.location.subway",
      hours: "Mon - Thu: 9:30AM - 6:00PM | Fri: 9:30AM - 5:00PM",
      servingKey: "footer.location.queens.serving",
    },
    {
      nameKey: "footer.location.nj",
      href: "/locations/new-jersey",
      address: "6035 Kennedy Blvd E",
      city: "West New York, NJ 07093",
      phone: "(848) 388-0660",
      hours: t("loc.nj.hours"),
      servingKey: "footer.location.nj.serving",
    },
    {
      nameKey: "footer.location.fl",
      href: "/locations/florida",
      address: "7284 West Palmetto Park Rd, Suite 101",
      city: "Boca Raton, FL 33433",
      phone: "(212) 586-0500",
      hours: "By Appointment",
      servingKey: "footer.location.fl.serving",
    },
  ];

  return (
    <footer id="contact" className="bg-navy-radial text-white scroll-mt-header">
      {/* Main Footer */}
      <div className="container-custom py-16 lg:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img src={logoVertical} alt="Arandia Law Firm" className="h-20 md:h-28 w-auto object-contain mb-6" />
            <p className="text-white/70 text-sm leading-relaxed mb-6">{t("footer.description")}</p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 bg-white/10 hover:bg-gold rounded-full flex items-center justify-center transition-colors group"
                >
                  <social.icon className="w-5 h-5 text-white group-hover:text-navy transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-gold mb-4">{t("footer.quickLinks")}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  {(link as any).isRoute ? (
                    <LocalLink to={link.href} className="text-white/70 hover:text-gold transition-colors text-sm">
                      {link.name}
                    </LocalLink>
                  ) : (
                    <a href={link.href} className="text-white/70 hover:text-gold transition-colors text-sm">
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          {locations.map((location) => (
            <div key={location.nameKey}>
            <h4 className="font-heading text-lg font-semibold text-gold mb-4">
              <LocalLink to={location.href} className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-sm">
                {t(location.nameKey)}
              </LocalLink>
            </h4>
              <ul className="space-y-3 text-sm text-white/70">
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  <span>
                    {location.address}
                    <br />
                    {location.city}
                    {location.subway && location.subwayLabelKey && (
                      <>
                        <br />
                        <span className="text-white/50 text-xs">
                          {t(location.subwayLabelKey)}: {location.subway}
                        </span>
                      </>
                    )}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gold flex-shrink-0" />
                  <a
                    href={`tel:${location.phone.replace(/[^0-9]/g, "")}`}
                    className="hover:text-gold transition-colors"
                  >
                    {location.phone}
                  </a>
                </li>
                <li className="text-xs text-white/50 mt-2 pl-7">{location.hours}</li>
                {location.servingKey && <li className="text-xs text-gold/60 mt-1 pl-7">{t(location.servingKey)}</li>}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">{t("footer.copyright")}</p>
          <div className="flex items-center gap-6 text-sm text-white/50">
            <LocalLink to="/privacy-policy" className="hover:text-gold transition-colors">
              {t("footer.privacy")}
            </LocalLink>
            <LocalLink to="/terms-and-conditions" className="hover:text-gold transition-colors">
              {t("footer.terms")}
            </LocalLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
